import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Stack,
    IconButton,
    Input,
    CircularProgress,
} from "@mui/material";
import { Upload, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { createPressNewsData, updatePressNewsData, getPressNewsById } from "../../Services/PressManagement/PressApi";
import { useGlobalSnackbar } from "../../Components/Common/GlobalSanckbar";

const AddEditPressNewsData = () => {
    const navigate = useNavigate();
    const { showSnackbar } = useGlobalSnackbar();
    const location = useLocation();
    const editData = location.state?.news;

    const [title, setTitle] = useState(editData?.title || "");
    const [link, setLink] = useState(editData?.link || "");
    const [description, setDescription] = useState(editData?.description || "");
    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(editData?.image || null);
    const [loading, setLoading] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    // Refs for smooth scroll to each error field
    const titleRef = useRef(null);
    const linkRef = useRef(null);
    const descriptionRef = useRef(null);
    const imageRef = useRef(null);

    useEffect(() => {
        if (editData && !editData.description && editData.id) {
            // If we have an ID but no description (e.g. from list view might be truncated), fetch full details
            const fetchDetails = async () => {
                try {
                    const response = await getPressNewsById(editData.id);
                    if (response && response.success && response.data) {
                        setDescription(response.data.description || "");
                        // Update other fields if needed
                        setTitle(response.data.title || "");
                        setLink(response.data.link || "");
                        setImagePreview(response.data.image || null);
                    }
                } catch (err) {
                    console.error("Error fetching news details:", err);
                }
            };
            fetchDetails();
        }
    }, [editData]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith("image/")) {
                setFieldErrors(prev => ({ ...prev, image: "Only image files (PNG, JPG, WEBP, SVG, GIF) are allowed" }));
                showSnackbar("Only image files are allowed", "error");
                e.target.value = "";
                return;
            }

            setImage(file);
            const reader = new FileReader();
            reader.onloadend = () => setImagePreview(reader.result);
            reader.readAsDataURL(file);
            // Clear image error when user selects a new image
            if (fieldErrors.image) {
                setFieldErrors(prev => ({ ...prev, image: undefined }));
            }
        }
    };

    const handleRemoveImage = () => {
        setImage(null);
        setImagePreview(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (image && !image.type.startsWith("image/")) {
            setFieldErrors(prev => ({ ...prev, image: "Only image files are allowed" }));
            showSnackbar("Only image files are allowed", "error");
            return;
        }

        setLoading(true);

        const formData = new FormData();
        formData.append("title", title);
        formData.append("link", link);
        formData.append("description", description);

        if (image) {
            formData.append("pressNewsImage", image);
        }

        try {
            if (editData) {
                const id = editData.id || editData._id;
                await updatePressNewsData(id, formData);
                showSnackbar("News updated successfully!", "success");
            } else {
                await createPressNewsData(formData);
                showSnackbar("News created successfully!", "success");
            }

            setTimeout(() => navigate("/press-management/news"), 1500);
        } catch (err) {
            const backendError = err.response?.data;
            if (backendError && backendError.details && Array.isArray(backendError.details)) {
                const errors = {};
                let firstScrollField = null;

                backendError.details.forEach(detail => {
                    let field = detail.field;
                    // Map server image field names to the local "image" key
                    if (
                        field === 'pressNewsImage' ||
                        (field === 'general' && detail.message.toLowerCase().includes('image'))
                    ) {
                        field = 'image';
                    }
                    errors[field] = detail.message;
                    if (!firstScrollField) firstScrollField = field;
                });

                setFieldErrors(errors);

                // Smooth scroll to the first errored field
                setTimeout(() => {
                    const refMap = {
                        title: titleRef,
                        link: linkRef,
                        description: descriptionRef,
                        image: imageRef,
                    };
                    const ref = refMap[firstScrollField];
                    if (ref?.current) {
                        ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 50);
            } else {
                showSnackbar(err.response?.data?.message || "Failed to save news data.", "error");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: "#FBFBFB", minHeight: "100vh" }}>



            <Typography sx={{ fontSize: "1.8rem", fontWeight: 500, mb: 4 }}>
                {editData ? "Edit Press News" : "Add New Press News"}
            </Typography>

            <Paper sx={{ p: 4, borderRadius: 3, border: "1px solid #E8E8E8", maxWidth: 800, mx: "auto" }}>
                {/* Top-level alert removed: errors are shown inline at each field */}
                {/* {success && <Alert severity="success" sx={{ mb: 3 }}>News saved successfully!</Alert>} */}

                <form onSubmit={handleSubmit}>
                    <Stack spacing={3}>
                        <Box ref={titleRef}>
                            <Typography sx={{ mb: 1, fontWeight: 500 }}>Title</Typography>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Enter news title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                error={!!fieldErrors.title}
                                helperText={fieldErrors.title}
                            />
                        </Box>

                        <Box ref={linkRef}>
                            <Typography sx={{ mb: 1, fontWeight: 500 }}>Link</Typography>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Enter news link"
                                value={link}
                                onChange={(e) => setLink(e.target.value)}
                                error={!!fieldErrors.link}
                                helperText={fieldErrors.link}
                            />
                        </Box>

                        <Box ref={descriptionRef}>
                            <Typography sx={{ mb: 1, fontWeight: 500 }}>Description</Typography>
                            <ReactQuill
                                theme="snow"
                                value={description}
                                onChange={setDescription}
                                style={{ height: "200px", marginBottom: fieldErrors.description ? "105px" : "85px" }}
                                modules={{
                                    toolbar: [
                                        [{ header: [1, 2, false] }],
                                        ["bold", "italic", "underline"],
                                        [{ list: "ordered" }, { list: "bullet" }],
                                        ["link", "clean"]
                                    ],
                                }}
                            />
                            {fieldErrors.description && (
                                <Typography variant="caption" color="error" sx={{ ml: 1, mt: 1, display: 'block' }}>
                                    {fieldErrors.description}
                                </Typography>
                            )}
                        </Box>

                        <Box ref={imageRef} sx={{ mb: 3 }}>
                            <Typography sx={{ mb: 1.5, fontWeight: 500 }}>News Image</Typography>
                            {!imagePreview ? (
                                <Box component="label"
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        border: fieldErrors.image ? "2px dashed #d32f2f" : "2px dashed #E0E0E0",
                                        borderRadius: 2,
                                        p: 4,
                                        textAlign: "center",
                                        cursor: "pointer",
                                        bgcolor: "#FAFAFA",
                                        transition: "all 0.2s",
                                        "&:hover": {
                                            borderColor: "#FFBD65",
                                            bgcolor: "#FFFDF9"
                                        }
                                    }}
                                >
                                    <Input type="file" inputProps={{ accept: "image/png, image/jpeg, image/jpg, image/webp, image/gif, image/svg+xml" }} sx={{ display: "none" }} onChange={handleImageChange} />
                                    <Upload size={32} color={fieldErrors.image ? "#d32f2f" : "#888"} style={{ marginBottom: 8 }} />
                                    <Typography color={fieldErrors.image ? "error" : "textSecondary"} sx={{ fontWeight: 500, fontSize: "0.9rem" }}>Click to upload image</Typography>
                                </Box>
                            ) : (
                                <Box sx={{ position: "relative", width: "fit-content", mx: "auto" }}>
                                    <img src={imagePreview} alt="Preview" style={{ width: "300px", height: "300px", objectFit: "cover", borderRadius: 12, display: "block" }} />
                                    <IconButton disableFocusRipple disableRipple disableTouchRipple
                                        onClick={handleRemoveImage}
                                        size="small"
                                        sx={{
                                            position: "absolute",
                                            top: 8,
                                            right: 8,
                                            bgcolor: "#FFFFFF",
                                            color: "#EF4444",
                                            border: "1px solid #E2E8F0",
                                            boxShadow: "none",
                                            "&:hover": {
                                                bgcolor: "#FEE2E2",
                                                color: "#DC2626"
                                            }
                                        }}
                                    >
                                        <X size={16} color="#EF4444" />
                                    </IconButton>
                                </Box>
                            )}
                            {fieldErrors.image && (
                                <Typography variant="caption" color="error" sx={{ ml: 1, mt: 1, display: 'block', textAlign: 'center' }}>
                                    {fieldErrors.image}
                                </Typography>
                            )}
                        </Box>

                        {/* <FormControlLabel
                            control={<Switch checked={status} onChange={(e) => setStatus(e.target.checked)} />}
                            label="Active Status"
                        /> */}

                        <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
                            <Button fullWidth variant="outlined" onClick={() => navigate("/press-management/news")}>
                                Cancel
                            </Button>
                            <Button fullWidth type="submit" variant="contained" disabled={loading} sx={{ bgcolor: "#FFBD65", color: "#000" }}>
                                {loading ? <CircularProgress size={20} /> : "Save News"}
                            </Button>
                        </Stack>
                    </Stack>
                </form>
            </Paper>
        </Box>
    );
};

export default AddEditPressNewsData;
